/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Component, memo, useCallback, useState} from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles} from '@material-ui/core/styles';
import MenuIcon from '@material-ui/icons/Menu';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {useMedia} from '../../hooks/useMedia';
import Hidden from '../../layout/Hidden';
import {dlsAlt, dlsColor, dlsLayout, dlsZIndex} from '../../theme/theme';
import {MenuItem, MenuItems} from '../../types/components';
import {SideSheetView} from '../../types/types';
import SideSheet from '../sidesheet/SideSheet';
import NavigationItem from './NavigationItem';
import NavigationSwitcher from './NavigationSwitcher';
import useExpandCollapse, {toggleCollapseStyle} from './useExpandCollapse';
import navStyles from './Navigation.module.scss';

export interface SideNavProps {
  onToggleCollapse?: Function;
};

// defines the props for primary and secondary navigation containers
export interface NavigationTypes extends SideNavProps {
  menuItems: MenuItems,
  // [DLS PACKAGE TODO]: Compact arguments to typed parameters object
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem): void;
  selectedIndex: number;
  activeIndex?: number;
  overrideStyle: GenericObject;
  hasMultipleSubModule: boolean,
  sideSheetContent?: () => JSX.Element;
  sideSheetMobileSwipeUp?: Component | unknown;
  sideSheetStatus?: {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetStatus: 'closed' | 'half' | 'open';
  };
  toggleSideSheet?: Function;
  geoFencePanelOpen?: boolean;
  showFindNearByPopup?: boolean;
  isUserProfileView?: boolean;
  useExpandCollapseFeature?: boolean;
  findButtonStatus?: boolean;
  isInitialized?: Boolean;
  isLoaded?: Boolean;
  secondaryNav?: {
    pages: MenuItems;
    show: boolean;
  };
  closeSecondaryNavigation?: () => void;
  currentPage: string;
  getPages: Function;
  isSideSheetArrowVisible?: boolean;
  sideSheetView?: SideSheetView
  isMobileFilterActive?: boolean;
  fullHeight?: boolean;
  isiOn?: boolean;
  isDrawerActive?: boolean;
  toolTipArrow?: boolean;
  toolTipPosition?: string;
  isCtcAdmin?: boolean;
  fullHeightIcons?: boolean;
};

interface StyleProps {
  fullHeight?: boolean,
  isPhone?: boolean,
  collapsed?: boolean,
  useExpandCollapseFeature?: boolean
};

const getPaperMinWidth = collapsed => {
  const collapsedNavWidth = dlsLayout('collapsedSidebar').width;
  const expandedNavWidth  =  dlsLayout('sidebar').width;
  const toggleFunc        = toggleCollapseStyle(collapsedNavWidth, expandedNavWidth);
  return toggleFunc({collapsed});
};

// defines the styles used by primary navigation container
const useStyles = makeStyles((theme) => ({
  root : {
    display : 'flex'
  },
  sideNavRoot : {
    zIndex : `${dlsZIndex('sideNavIndex')} !important` as any
  },
  nonUserProfileView : {
    position : 'fixed',
    top      : 2,
    left     : 12,
    zIndex   : dlsZIndex('navigationNonUserProfileView')
  },
  drawerPaper : {
    '&&' : {
      overflowY                      : 'initial',
      backgroundColor                : `${dlsColor('grey1')} !important`,
      height                         : '100vh',
      zIndex                         : dlsZIndex('navigationDrawerPaper'),
      transition                     : 'width 0.2s ease-in-out',
      [theme.breakpoints.down('sm')] : {
        top    : `${dlsLayout('header').mobile}px !important`,
        height : ({fullHeight, isPhone}: StyleProps) => fullHeight && isPhone ? 'calc(100vh - 48px)' : 'calc(100vh - 56px)'
      },
      [theme.breakpoints.up('md')] : {
        top   : `${dlsLayout('header').desktop}px !important`,
        width : ({useExpandCollapseFeature, collapsed}: StyleProps) =>
          useExpandCollapseFeature ? getPaperMinWidth(collapsed) : 106
      }
    }
  },
  wrapper : {
    display        : 'flex',
    justifyContent : 'space-between',
    flexDirection  : 'column',
    height         : '100%'
  },
  drawerPaperAnchorLeft : {
    borderRight : 'none'
  },
  content : {
    flexGrow : 1,
    padding  : dlsAlt('layout', 'spacing')
  }
}));

/**
 * @Navigation
 *
 * Defines the Navigation Component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   const navItems = [
 *     {icon: <Page1Icon>, label: t('page1:title), pages: '/pages1' },
 *     {icon: <Page2Icon>, label: t('page2:title), pages: '/pages2' },
 *   ];
 *
 *    <Navigation menuItems={navItems}
 *      onSetPage={onSetPage}
 *      selectedIndex={selectedIndex}
 *    />
 *
 * ```
 */
const Navigation = ({
  menuItems,
  onSetPage,
  selectedIndex,
  activeIndex,
  hasMultipleSubModule,
  sideSheetContent,
  sideSheetStatus,
  toggleSideSheet,
  geoFencePanelOpen,
  showFindNearByPopup,
  isUserProfileView,
  findButtonStatus,
  isInitialized,
  isLoaded,
  secondaryNav,
  closeSecondaryNavigation,
  currentPage,
  getPages,
  overrideStyle,
  isSideSheetArrowVisible,
  sideSheetView,
  isMobileFilterActive,
  sideSheetMobileSwipeUp,
  isiOn,
  onToggleCollapse,
  fullHeight = false,
  useExpandCollapseFeature = false,
  isDrawerActive = true,
  toolTipPosition = 'bottom',
  toolTipArrow = false,
  isCtcAdmin = false,
  fullHeightIcons = false
}: NavigationTypes) => {
  const {isDesktop, isPhone}        = useMedia();
  const [mobileOpen, setMobileOpen] = useState(false);
  const showSecondaryNavigation     = secondaryNav?.show;
  const isSideSheetValid = (
    (!isiOn || !isDesktop) &&
    !isUserProfileView &&
    isDrawerActive
  );
  const {
    classes: expandClasses,
    collapsed,
    onHandleCollapse
  } = useExpandCollapse(useExpandCollapseFeature, onToggleCollapse);

  const classes = useStyles({
    fullHeight,
    isPhone,
    collapsed,
    useExpandCollapseFeature
  });

  const handleDrawerToggle = function() {
    setMobileOpen(!mobileOpen);
  };

  const container =
    window !== undefined
      ? function() {
        return window.document.body;
      }
      : undefined;

  const NavigationAccent = () => (
    <div style={{display: 'block'}}>
      <div className={navStyles.accent} />
      <div className={navStyles.bottomAccent} />
    </div>
  );

  // [DLS PACKAGE TODO]: Refactor to parameters object
  // eslint-disable-next-line max-params
  const setPage = useCallback(function(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem) {
    if (!isDesktop && !item?.pages) {
      setMobileOpen(false);
    }
    onSetPage(page, index, isSecondaryIndex || showSecondaryNavigation);
  }, [onSetPage, menuItems, isDesktop, showSecondaryNavigation]);

  return (
    <div className={classes.root}
      style={{marginTop: isDesktop ? dlsLayout('header').height : 0}}>
      <Hidden show='mobile'>
        {!isUserProfileView &&
        <div className={classes.nonUserProfileView}>
          <IconButton color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}>
            <MenuIcon />
          </IconButton>
        </div>}
      </Hidden>
      <nav>
        <Hidden show='mobile'>
          <Drawer container={container}
            variant='temporary'
            anchor='left'
            open={mobileOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper : classnames(classes.drawerPaper, navStyles.mobileNav),
              root  : classes.sideNavRoot
            }}
            ModalProps={{
              keepMounted : true
            }}>
            {showSecondaryNavigation && <NavigationSwitcher label={translate('nav:navigationSwitch.ctaLabel')}
              handleClick={closeSecondaryNavigation} />}
            <NavigationItem fullHeight={fullHeightIcons}
              selectedIndex={selectedIndex}
              isCtcAdmin={isCtcAdmin}
              activeIndex={activeIndex}
              overrideStyle={overrideStyle}
              hasMultipleSubModule={hasMultipleSubModule}
              menuItems={!showSecondaryNavigation
                ? menuItems : getPages(menuItems[activeIndex]?.pages || secondaryNav?.pages,
                  currentPage) as unknown as MenuItems}
              onSetPage={setPage}
              toggleSideSheet={toggleSideSheet}
              closeSecondaryNavigation={closeSecondaryNavigation}
              showSecondaryNavigation={showSecondaryNavigation}
              secondaryNavOpen={null}
              currentPage={currentPage}
              useExpandCollapseFeature={useExpandCollapseFeature}
              expandClasses={expandClasses}
              collapsed={collapsed}
              onHandleCollapse={onHandleCollapse}
              sideSheetStatus={sideSheetStatus}
              toolTipArrow={toolTipArrow}
              toolTipPosition={toolTipPosition} />
          </Drawer>
        </Hidden>
        <Hidden show='desktop'>
          <Drawer variant='permanent'
            classes={{
              paper                 : classes.drawerPaper,
              paperAnchorDockedLeft : classes.drawerPaperAnchorLeft

            }}
            open>
            <div className={classes.wrapper}>
              <NavigationItem fullHeight={fullHeightIcons}
                menuItems={menuItems}
                isCtcAdmin={isCtcAdmin}
                activeIndex={activeIndex}
                overrideStyle={overrideStyle}
                selectedIndex={selectedIndex}
                hasMultipleSubModule={hasMultipleSubModule}
                onSetPage={onSetPage}
                toggleSideSheet={toggleSideSheet}
                sideSheetStatus={sideSheetStatus}
                secondaryNavOpen={showSecondaryNavigation}
                closeSecondaryNavigation={closeSecondaryNavigation}
                isInitialized={isInitialized}
                currentPage={currentPage}
                isLoaded={isLoaded}
                useExpandCollapseFeature={useExpandCollapseFeature}
                expandClasses={expandClasses}
                collapsed={collapsed}
                onHandleCollapse={onHandleCollapse}
                isSideSheetArrowVisible={isSideSheetArrowVisible}
                toolTipArrow={toolTipArrow}
                toolTipPosition={toolTipPosition} />
              {!useExpandCollapseFeature && <NavigationAccent />}
            </div>
          </Drawer>
        </Hidden>
      </nav>
      {isSideSheetValid &&
      <SideSheet sideSheetStatus={sideSheetStatus}
        menuItems={menuItems}
        selectedIndex={selectedIndex}
        toggleSideSheet={toggleSideSheet}
        geoFencePanelOpen={geoFencePanelOpen}
        showFindNearByPopup={showFindNearByPopup}
        findButtonStatus={findButtonStatus}
        isInitialized={isInitialized}
        isLoaded={isLoaded}
        sideSheetView={sideSheetView}
        sideSheetMobileSwipeUp={sideSheetMobileSwipeUp}
        fullHeight={fullHeight}
        isMobileFilterActive={isMobileFilterActive}>
        {sideSheetContent?.()}
      </SideSheet>}
    </div>
  );
};

export default memo(Navigation);
